<script setup lang="ts">
import { motion } from 'motion-v'

const { t, tm } = useI18n()

useHead({
  link: [{
    rel: 'canonical',
    href: `${import.meta.env.VITE_MONOLITH_URL}/how-it-works`,
  }],
})

useSeoMeta({
  title: t('how_it_works.meta.title'),
  description: t('how_it_works.meta.description'),
  ogTitle: t('how_it_works.meta.title'),
  ogDescription: t('how_it_works.meta.description'),
  ogImage: `${S3_ASSETS_URL}/meta-default.jpg`,
})
</script>

<template>
  <div class="isolate">
    <LayoutNavbar />

    <section class="mb-8 grid gap-y-20 bg-gradient-to-b from-[#F8F8F0] to-[#FDFDFB] px-4 pt-10 shadow-2xl shadow-black/10 lg:mb-16 lg:rounded-b-[5rem] lg:px-8 lg:py-10">
      <motion.header
        class="mx-auto grid max-w-screen-lg place-items-center gap-y-8 text-center"
        :initial="{
          opacity: 0,
          y: '10%',
        }"
        :while-in-view="{
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.4,
            ease: 'easeOut'
          }
        }"
      >
        <h1 class="mx-auto max-w-screen-sm text-3xl font-medium !leading-tight lg:text-5xl">
          {{ t('how_it_works.title') }}
        </h1>

        <p class="text-lg text-neutral-600 lg:text-xl">
          {{ t('how_it_works.description') }}
        </p>

        <UiButton
          type="button"
          color="accent"
          class="group"
          as-child
        >
          <RouterLink :to="{ name: '/free-demo'}">
            {{ t('how_it_works.action') }}

            <UiIcon
              name="arrow-forward-rounded"
              class="transition-transform duration-200 group-hover:translate-x-1"
            />
          </RouterLink>
        </UiButton>
      </motion.header>

      <div class="isolate mx-auto max-w-screen-xl [counter-reset:timeline]">
        <HowItWorksTimelineItem
          v-for="(timelineItem, timelineIndex) in tm('how_it_works.timeline')"
          :key="timelineIndex"
          v-bind="{timelineIndex, timelineItem}"
        />
      </div>

      <motion.section
        class="-mx-4 max-w-screen-xl lg:mx-auto lg:w-full lg:px-4 lg:pb-8"
        :initial="{ opacity: 0, y: 50, scale: 0.5 }"
        :while-in-view="{ opacity: 1, y: 0, scale: 1 }"
        :exit="{ opacity: 0, y: 50, scale: 0.5 }"
      >
        <div class="relative overflow-clip px-8 py-12 lg:rounded-3xl">
          <div class="absolute inset-0 isolate before:absolute before:inset-0 before:z-[1] before:bg-gray-800/70">
            <img
              :src="`${S3_ASSETS_URL}/home/cta-02.jpg`"
              class="aspect-video size-full object-cover object-center"
              role="presentation"
            >
          </div>

          <div class="isolate mx-auto grid max-w-screen-md place-items-center gap-y-8">
            <h3 class="text-balance text-center text-3xl leading-tight text-white lg:text-5xl">
              {{ t('how_it_works.cta_banner.title') }}
            </h3>

            <p class="text-balance text-center text-lg leading-tight text-white lg:text-xl">
              {{ t('how_it_works.cta_banner.description') }}
            </p>

            <UiButton
              class="group w-full max-w-xs lg:max-w-max"
              as-child
            >
              <RouterLink :to="{ name: '/free-demo' }">
                {{ t('how_it_works.cta_banner.action') }}

                <UiIcon
                  name="arrow-forward-rounded"
                  class="transition-transform duration-200 group-hover:translate-x-1"
                />
              </RouterLink>
            </UiButton>
          </div>
        </div>
      </motion.section>
    </section>

    <LayoutFooter />
  </div>
</template>
